import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import clsx from "clsx";

const FADE_DELAY = 200;
const VIEWPORT_MARGIN = 8;

export default function Tooltip({ content, children, className }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const hideTimer = useRef();
  const triggerRef = useRef();
  const tooltipRef = useRef();

  const setOpenTooltip = (visible) => {
    clearTimeout(hideTimer.current);
    if (visible) {
      setOpen(true);
      hideTimer.current = setTimeout(() => setHover(true), 0);
    } else {
      setHover(false);
      hideTimer.current = setTimeout(() => setOpen(false), FADE_DELAY);
    }
  };

  // Le tooltip est rendu en portal (hors du flux du document) pour ne jamais
  // impacter la mise en page environnante ni être rogné par un ancêtre en
  // overflow: hidden. Sa position est donc recalculée manuellement par-dessus
  // son déclencheur, en le recalant si besoin pour rester dans la fenêtre.
  useEffect(() => {
    if (!open || !tooltipRef.current || !triggerRef.current) {
      return;
    }
    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    tooltipEl.style.left = `${trigger.left}px`;
    tooltipEl.style.top = `${trigger.bottom + 4}px`;

    const tooltipRect = tooltipEl.getBoundingClientRect();
    if (tooltipRect.right > window.innerWidth - VIEWPORT_MARGIN) {
      tooltipEl.style.left = `${Math.max(VIEWPORT_MARGIN, window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN)}px`;
    }
  }, [open]);

  return (
    <div
      ref={triggerRef}
      className={clsx("inline-block", className)}
      onMouseEnter={() => setOpenTooltip(true)}
      onMouseLeave={() => setOpenTooltip(false)}
      onClick={() => setOpenTooltip(!hover)}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`fixed select-none px-2.5 py-1.5 rounded text-white bg-main-color md:min-w-[480px] z-[9999999] transition-opacity ease-in-out duration-[${FADE_DELAY}ms] ${hover ? "opacity-100" : "opacity-0"}`}
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
}

Tooltip.propTypes = {
  content: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
